import React, { FC } from 'react';
import { useLocation } from 'react-router-dom';

import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import {
  apiGroomingSearchRetrieve,
  getApiGroomingSearchRetrieveQueryKey,
  useApiGroomingSearchRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-grooming-search-retrieve';
import {
  apiSeoLocationByPathFaqsList,
  getApiSeoLocationByPathFaqsListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-by-path-faqs-list';
import {
  apiSeoLocationByPathReviewsList,
  getApiSeoLocationByPathReviewsListQueryKey,
  useApiSeoLocationByPathReviewsList,
} from '@rover/rsdk/src/apiClient/latest/api-seo-location-by-path-reviews-list';
import {
  FLAGS,
  isFlagActiveForConfig,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http404Error, Http500Error } from '@rover/utilities/HttpError';

import GroomingSeoLocationPageTemplate from '../../templates/GroomingSeoLocation/GroomingSeoLocationPageTemplate';
import { getGroomingSearchParams } from '../../templates/GroomingSeoLocation/utils/groomingSearch';
import { SeoLocationContainerProps, SeoLocationEngineContainer } from '../../types';
import { useSeoLocationSharedQueries } from '../../utils/seoLocationSharedQueries';

const GroomingSeoLocationContainer: FC<SeoLocationContainerProps> = ({ seoLocation }) => {
  const { isFlagActive } = useFrontendConfiguration();
  const { pathname } = useLocation();
  // The pages exist in the SEO data before the rollout opens them, so an unreleased
  // grooming page has to read as missing rather than render.
  if (!isFlagActive(FLAGS.ROLLOUT_GROOMING_SEO_LOCATION_PAGES)) {
    throw new Http404Error();
  }

  const {
    countryConfig,
    frontendConfig,
    isLoading: isLoadingShared,
    isError: isErrorShared,
    queryTags,
  } = useSeoLocationSharedQueries();
  const {
    isLoading: isLoadingReviews,
    isError: isErrorReviews,
    data: reviews,
  } = useApiSeoLocationByPathReviewsList({ path: pathname });

  const groomingSearchParams = getGroomingSearchParams(seoLocation);
  const { data: groomingSearchResponse } = useApiGroomingSearchRetrieve(groomingSearchParams, {
    query: { retry: false },
  });

  if (isErrorShared || isErrorReviews) {
    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: { pathname: getPath(), ...queryTags, isErrorReviews },
    });
    throw new Http500Error();
  }

  const isLoading = isLoadingShared || isLoadingReviews;
  if (isLoading) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage('SeoLocationPageContainer rendered with missing prefetches', {
        tags: { pathname: getPath(), ...queryTags, isLoadingReviews },
      });
    }

    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  // The checks above rule out a pending or failed query, so these have all landed — the
  // guard is what tells the compiler so.
  if (!reviews || !countryConfig || !frontendConfig) {
    throw new Http500Error();
  }

  return (
    <GroomingSeoLocationPageTemplate
      seoLocation={seoLocation}
      reviews={reviews.results!}
      countryConfig={countryConfig}
      frontendConfig={frontendConfig}
      groomingSearchResponse={groomingSearchResponse}
    />
  );
};

const reactQueryPrefetch: SeoLocationEngineContainer['reactQueryPrefetch'] = async (
  queryClient,
  { seoLocation, path }
) => {
  // The same query the shared prefetches pull, so react-query serves one request for
  // both. Raising here keeps a gated page from reaching the renderer at all.
  const frontendConfig = await queryClient.fetchQuery({
    queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
    queryFn: () => apiFrontendConfigurationRetrieve(),
  });
  if (
    !isFlagActiveForConfig(frontendConfig.flags ?? {}, FLAGS.ROLLOUT_GROOMING_SEO_LOCATION_PAGES)
  ) {
    throw new Http404Error();
  }

  return Promise.all([
    queryClient.prefetchQuery({
      queryKey: getApiGroomingSearchRetrieveQueryKey(getGroomingSearchParams(seoLocation)),
      queryFn: () => apiGroomingSearchRetrieve(getGroomingSearchParams(seoLocation)),
      // The page renders without groomers if the search fails, so skip the default
      // 3 retries (1s/2s/4s backoff) that would stall SSR before that fallback.
      retry: false,
    }),
    queryClient.prefetchQuery({
      queryKey: getApiSeoLocationByPathReviewsListQueryKey({ path }),
      queryFn: () => apiSeoLocationByPathReviewsList({ path }),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiSeoLocationByPathFaqsListQueryKey({ path }),
      queryFn: () => apiSeoLocationByPathFaqsList({ path }),
    }),
  ]);
};

export default Object.assign(GroomingSeoLocationContainer, { reactQueryPrefetch });
